import { useCallback, useEffect, useRef, type TouchEvent } from 'react';

export interface LongPressHandlers {
  onTouchStart: (e: TouchEvent) => void;
  onTouchMove: (e: TouchEvent) => void;
  onTouchEnd: () => void;
  onTouchCancel: () => void;
}

const LONG_PRESS_MS = 500;
/** Movement (px) beyond which the gesture is treated as a scroll, not a press. */
const MOVE_TOLERANCE = 10;

/**
 * Touch long-press → context-menu opener. Fires `onLongPress(x, y)` at the
 * touch point after ~500ms of a stationary single-finger press; cancels on
 * move past a small tolerance, on scroll, or on touch end/cancel. Returns
 * touch handlers to spread onto the target element.
 *
 * Guards for non-touch / jsdom environments: handlers are inert if there is no
 * timer support, and a passive document `scroll` listener (added only while a
 * press is pending) cancels the gesture when the page moves.
 */
export function useLongPress(onLongPress: (x: number, y: number) => void): LongPressHandlers {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const start = useRef<{ x: number; y: number } | null>(null);
  // Keep the latest callback without re-creating handlers each render.
  const cb = useRef(onLongPress);
  cb.current = onLongPress;

  const clear = useCallback((): void => {
    if (timer.current != null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    start.current = null;
  }, []);

  // Cancel a pending press if the page scrolls underneath the finger.
  useEffect(() => {
    const onScroll = (): void => clear();
    const opts: AddEventListenerOptions = { passive: true, capture: true };
    document.addEventListener('scroll', onScroll, opts);
    return () => document.removeEventListener('scroll', onScroll, opts);
  }, [clear]);

  // Tidy up any in-flight timer on unmount.
  useEffect(() => clear, [clear]);

  const onTouchStart = useCallback(
    (e: TouchEvent): void => {
      if (e.touches.length !== 1) {
        clear();
        return;
      }
      const t = e.touches[0];
      start.current = { x: t.clientX, y: t.clientY };
      if (timer.current != null) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        const s = start.current;
        timer.current = null;
        if (s) cb.current(s.x, s.y);
      }, LONG_PRESS_MS);
    },
    [clear],
  );

  const onTouchMove = useCallback(
    (e: TouchEvent): void => {
      const s = start.current;
      if (!s) return;
      const t = e.touches[0];
      if (!t) return;
      if (Math.abs(t.clientX - s.x) > MOVE_TOLERANCE || Math.abs(t.clientY - s.y) > MOVE_TOLERANCE) {
        clear();
      }
    },
    [clear],
  );

  return { onTouchStart, onTouchMove, onTouchEnd: clear, onTouchCancel: clear };
}
